import { assertNever } from "@impero-com/common/utils/obj";
import {
    encodeDataParam,
    CONTROL_ACTIVITIES_DATA_PARAM,
    controlActivitiesDateFilterCodec,
} from "@impero-com/ui/domain";
import moment from "moment-timezone";

import { IanaTimezoneName } from "api/generated/model";
import { FILTER_VALUE_DATE_FORMAT } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRangeUtils";

import {
    ControlActivitiesScope,
    buildControlActivitiesDefaultDateFilter,
} from "./consts";
import { ControlActivitiesDataParamsApi } from "./useControlActivitiesDataParams";

/**
 * A `[start, end]` range of `YYYY-MM-DD` date-only strings (either bound may be
 * `null` for an open-ended range), or `null` when no range is selected.
 */
export type DateRangeValue = [string | null, string | null] | null;

export type ActiveDateFilter = "dueDate" | "period";

export interface ControlActivitiesDateQueryParams {
    dueDateStart?: string;
    dueDateEnd?: string;
    periodDateStart?: string;
    periodDateEnd?: string;
}

type DateFilterVariant =
    | {
          activeFilter: "dueDate";
          dueDateRange: DateRangeValue;
          setDueDateRange: (value: DateRangeValue) => void;
      }
    | {
          activeFilter: "period";
          periodRange: DateRangeValue;
          setPeriodRange: (value: DateRangeValue) => void;
      };

export type ControlActivitiesDateFilters = DateFilterVariant & {
    setActiveFilter: (filter: ActiveDateFilter) => void;
    /**
     * `true` when no date param is set, i.e. the filter is the default 2 months
     * due date window
     */
    isDefault: boolean;
};

interface ControlActivitiesDateFiltersProps {
    dateFilters: ControlActivitiesDateFilters;
    queryParams: ControlActivitiesDateQueryParams;
}

/** Default due-date window: one month back and one month forward from today. */
function buildDefaultDueDateRange(): [string, string] {
    const today = moment();
    return [
        today.clone().subtract(1, "month").format(FILTER_VALUE_DATE_FORMAT),
        today.clone().add(1, "month").format(FILTER_VALUE_DATE_FORMAT),
    ];
}

/** Default period window: the current month. */
function buildDefaultPeriodRange(): [string, string] {
    const today = moment();
    return [
        today.clone().startOf("month").format(FILTER_VALUE_DATE_FORMAT),
        today.clone().endOf("month").format(FILTER_VALUE_DATE_FORMAT),
    ];
}

/** A range counts as "set" only when at least one bound is present. */
function hasBound(
    value: DateRangeValue
): value is [string | null, string | null] {
    return value !== null && (value[0] !== null || value[1] !== null);
}

function startOfDayIso(date: string, timezone: IanaTimezoneName): string {
    return moment
        .tz(date, FILTER_VALUE_DATE_FORMAT, timezone)
        .startOf("day")
        .format();
}

function endOfDayIso(date: string, timezone: IanaTimezoneName): string {
    return moment
        .tz(date, FILTER_VALUE_DATE_FORMAT, timezone)
        .endOf("day")
        .format();
}

/**
 * @param timezone The zone due dates are shown in, so a date range covers the
 * same days here as on the dashboards that link to this list.
 */
export function getControlActivitiesDateFilters(
    dataParams: ControlActivitiesDataParamsApi,
    scope: ControlActivitiesScope,
    timezone: IanaTimezoneName
): ControlActivitiesDateFiltersProps {
    const defaultDateFilter = buildControlActivitiesDefaultDateFilter(scope);
    const dateFilter = dataParams.values.dateFilter;
    const activeFilter: ActiveDateFilter = dateFilter?.mode ?? "dueDate";

    const dueDateRange: DateRangeValue =
        activeFilter === "dueDate"
            ? (dateFilter?.range ?? buildDefaultDueDateRange())
            : null;
    const periodRange: DateRangeValue =
        activeFilter === "period"
            ? (dateFilter?.range ?? buildDefaultPeriodRange())
            : null;

    const setActiveFilter = (filter: ActiveDateFilter) => {
        switch (filter) {
            case "period":
                dataParams.setValue(CONTROL_ACTIVITIES_DATA_PARAM.DATE_FILTER, {
                    mode: "period",
                    range: buildDefaultPeriodRange(),
                });
                break;
            case "dueDate":
                // Reset to this page's own default due-date window.
                dataParams.setValue(
                    CONTROL_ACTIVITIES_DATA_PARAM.DATE_FILTER,
                    defaultDateFilter
                );
                break;
            default:
                assertNever(filter);
        }
    };

    const setDueDateRange = (value: DateRangeValue) => {
        dataParams.setValue(
            CONTROL_ACTIVITIES_DATA_PARAM.DATE_FILTER,
            // Empty → revert to this page's own default due-date window.
            hasBound(value)
                ? { mode: "dueDate", range: value }
                : defaultDateFilter
        );
    };

    const setPeriodRange = (value: DateRangeValue) => {
        // Empty → revert to the default period (current month) so period stays
        // active instead of falling back to due date.
        const nextValue = hasBound(value) ? value : buildDefaultPeriodRange();
        dataParams.setValue(CONTROL_ACTIVITIES_DATA_PARAM.DATE_FILTER, {
            mode: "period",
            range: nextValue,
        });
    };

    const queryParams = ((): ControlActivitiesDateQueryParams => {
        switch (activeFilter) {
            case "period":
                // Period params are `NaiveDate` on the backend, so they take
                // bare `YYYY-MM-DD` strings (no time/zone) — how we store them.
                return {
                    periodDateStart: periodRange?.[0] ?? undefined,
                    periodDateEnd: periodRange?.[1] ?? undefined,
                };
            case "dueDate":
                return {
                    dueDateStart: dueDateRange?.[0]
                        ? startOfDayIso(dueDateRange[0], timezone)
                        : undefined,
                    dueDateEnd: dueDateRange?.[1]
                        ? endOfDayIso(dueDateRange[1], timezone)
                        : undefined,
                };
            default:
                return assertNever(activeFilter);
        }
    })();

    const variant = ((): DateFilterVariant => {
        switch (activeFilter) {
            case "dueDate":
                return { activeFilter, dueDateRange, setDueDateRange };
            case "period":
                return { activeFilter, periodRange, setPeriodRange };
            default:
                return assertNever(activeFilter);
        }
    })();

    const dateFilters: ControlActivitiesDateFilters = {
        ...variant,
        setActiveFilter,
        // Omits the param from the URL when the filter is the default for this page
        isDefault:
            encodeDataParam(
                controlActivitiesDateFilterCodec,
                dateFilter,
                defaultDateFilter
            ) === null,
    };

    return { dateFilters, queryParams };
}
